import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Container, Reveal, PageHeader } from '@/components/ui'
import ImageViewer from '@/components/magazine/ImageViewer'
import { findMagazineBySlug, type ImageMagazineIssue, type MagazineIssue } from '@/content/magazines'
import SEO from '@/utils/seo'
import { buildArticle, buildBreadcrumbList } from '@/utils/structuredData'
import { cmsMediaIndex } from '@/content/cms/media.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { safeExternalUrl } from '@/utils/safeExternalUrl'
import { usePreload } from '@/entry-server'

/**
 * MagazineDetailPage — image-clipping press detail at /magazine/:slug.
 *
 * Fetches full magazine data from /api/cms/public/magazines/:slug?locale=...
 * at mount (re-fetches on slug or locale change). Falls back to the static
 * `findMagazineBySlug` import if the API returns 404. Renders only `image`
 * kind magazines; other kinds redirect to `/magazine`.
 */
export default function MagazineDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { i18n } = useTranslation()
  const locale = i18n.language as 'zh-Hant' | 'zh-Hans' | 'en'
  // PR2 Phase 7: prerender passes the full CMS row via PreloadContext; CSR
  // path (preloaded is null) keeps the existing fetch + static fallback logic.
  const preloaded = usePreload()
  const [magazineData, setMagazineData] = useState<MagazineIssue | null>(
    preloaded?.kind === 'magazine'
      ? mapCmsMagazine((preloaded.data as PublicMagazineResp).magazine, locale)
      : null,
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    // Preload path: data already populated at build time, skip the fetch.
    if (preloaded) return
    let cancelled = false
    setMagazineData(null)
    setError(null)

    fetch(`/api/cms/public/magazines/${encodeURIComponent(slug)}?locale=${locale}${previewTokenParam()}`)
      .then(async (r) => {
        if (r.status === 404) {
          // CMS does not have this slug — fall back to static source.
          const fallback = findMagazineBySlug(slug)
          if (!fallback) throw new Error('http_404')
          if (cancelled) return
          setMagazineData(fallback)
          return
        }
        if (!r.ok) throw new Error(`http_${r.status}`)
        const j = (await r.json()) as PublicMagazineResp
        if (cancelled) return
        setMagazineData(mapCmsMagazine(j.magazine, locale))
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof Error && err.message === 'http_404') {
          setError('http_404')
          return
        }
        // Network / parse failure — try the static fallback once before erroring.
        const fallback = slug ? findMagazineBySlug(slug) : undefined
        if (fallback) {
          setMagazineData(fallback)
          return
        }
        setError(err instanceof Error ? err.message : 'fetch_failed')
      })

    return () => {
      cancelled = true
    }
  }, [slug, locale, preloaded])

  if (error === 'http_404' || (!magazineData && error === null && !slug)) {
    return <Navigate to="/magazine" replace />
  }

  if (error) {
    return (
      <section className="py-section-y px-section-x">
        <Container>
          <p className="text-danger">無法載入雜誌：{error}</p>
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
            onClick={() => {
              setError(null)
              setMagazineData(null)
              // Re-trigger effect by toggling a key-like bump — simplest path:
              window.location.reload()
            }}
          >
            <Icon icon="lucide:refresh-cw" />
            重試
          </button>
        </Container>
      </section>
    )
  }

  if (!magazineData) {
    return (
      <section className="py-section-y px-section-x">
        <Container>
          <p className="text-ink-muted">載入中…</p>
        </Container>
      </section>
    )
  }

  if (magazineData.kind !== 'image') {
    return <Navigate to="/magazine" replace />
  }

  return <ImageMagazineDetail mag={magazineData as ImageMagazineIssue} />
}

interface PublicMagazineResp {
  locale: string
  magazine: CmsMagazinePayload
}

interface CmsMagazinePayload {
  slug: string
  kind: string
  publication_zhHant: string | null
  publication_zhHans: string | null
  publication_en: string | null
  issue_zhHant: string | null
  issue_zhHans: string | null
  issue_en: string | null
  title_zhHant: string | null
  title_zhHans: string | null
  title_en: string | null
  interviewee_zhHant: string | null
  interviewee_zhHans: string | null
  interviewee_en: string | null
  summary_zhHant: string | null
  summary_zhHans: string | null
  summary_en: string | null
  cover_media_id: number | null
  cover_media_uuid: string | null
  external_url: string | null
  pdf_path: string | null
  issue_date: string | null
  published_at: number | null
}

type LocalizedField = 'publication' | 'issue' | 'interviewee' | 'title' | 'summary'

function pickLocale(
  payload: CmsMagazinePayload,
  field: LocalizedField,
  locale: 'zh-Hant' | 'zh-Hans' | 'en',
): string {
  const zh = payload[`${field}_zhHant`]
  const hans = payload[`${field}_zhHans`]
  const en = payload[`${field}_en`]
  if (locale === 'en') return en ?? zh ?? ''
  if (locale === 'zh-Hans') return hans ?? zh ?? en ?? ''
  return zh ?? hans ?? en ?? ''
}

function mapCmsMagazine(
  api: CmsMagazinePayload,
  locale: 'zh-Hant' | 'zh-Hans' | 'en',
): ImageMagazineIssue {
  const date = (() => {
    if (!api.issue_date) return ''
    const t = new Date(api.issue_date).getTime()
    return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : api.issue_date
  })()

  const coverUuid = api.cover_media_uuid ?? undefined
  const media = coverUuid ? cmsMediaIndex[coverUuid] : undefined
  const coverImage = media?.urls.original ?? media?.urls.medium ?? media?.urls.thumb

  return {
    kind: 'image',
    slug: api.slug,
    publication: pickLocale(api, 'publication', locale),
    publicationEn: api.publication_en ?? '',
    issue: pickLocale(api, 'issue', locale),
    issueEn: api.issue_en ?? '',
    interviewee: pickLocale(api, 'interviewee', locale),
    title: pickLocale(api, 'title', locale),
    titleEn: api.title_en ?? '',
    summary: pickLocale(api, 'summary', locale),
    summaryEn: api.summary_en ?? '',
    date,
    coverImage: coverImage ?? '',
    coverMediaUuid: coverUuid,
    coverWidth: 1200,
    coverHeight: 800,
    externalUrl: api.external_url ?? '',
  } as unknown as ImageMagazineIssue
}

function ImageMagazineDetail({ mag }: { mag: ImageMagazineIssue }) {
  const { t, i18n } = useTranslation()
  const publicationLabel = i18n.language.startsWith('zh') ? mag.publication : mag.publicationEn
  const issueLabel = i18n.language.startsWith('zh') ? mag.issue : mag.issueEn

  const formattedDate = (() => {
    try {
      return new Date(mag.date).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return mag.date
    }
  })()

  const coverAlt = t('magazine.coverAlt', {
    publication: publicationLabel,
    issue: issueLabel,
  })

  // The publisher's URL comes from the CMS, so it is validated before it is
  // allowed to become an href — a `javascript:` value would run script in
  // every visitor's browser. This page IS the entry's own page, so there is
  // no internal fallback to offer: when the value does not pass, the
  // "read at the publisher" links are simply not rendered.
  const externalHref = safeExternalUrl(mag.externalUrl)

  return (
    <article aria-labelledby="magazine-detail-title">
      <SEO
        title={`${mag.title} — ${publicationLabel}`}
        description={mag.summary}
        keywords={t('seo.magazineKeywords')}
        type="article"
        ogImage={mag.coverImage}
        articlePublishedTime={mag.date || undefined}
        articleAuthor={mag.interviewee || undefined}
        jsonLd={[
          buildArticle({
            path: `/magazine/${mag.slug}`,
            headline: mag.title,
            // alternativeHeadline is per-locale; ImageMagazineIssue doesn't
            // surface titleEn so we leave it unset for now. The page-level
            // <title> already encodes the publication for SERP context.
            datePublished: mag.date || new Date().toISOString().slice(0, 10),
            author: mag.interviewee || t('common.brand'),
            image: mag.coverImage,
            section: '媒體報導 Press Coverage',
            keywords: [
              'YADA',
              '教育傳媒',
              '媒體報導',
              publicationLabel,
              issueLabel,
              mag.interviewee,
            ].filter(Boolean) as string[],
            type: 'NewsArticle',
          }),
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.magazine'), path: '/magazine' },
            { name: mag.title, path: `/magazine/${mag.slug}` },
          ]),
        ]}
      />

      <PageHeader
        tag={publicationLabel}
        title={mag.title}
        description={mag.summary}
      />

      {/* Meta + external link */}
      <section className="py-5 px-section-x bg-surface-2 border-b border-border">
        <Container size="lg">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm flex-1">
                <div className="flex items-start gap-2">
                  <Icon icon="lucide:book-open" className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                      {t('magazine.meta.publication')}
                    </dt>
                    <dd className="text-ink font-semibold">{publicationLabel}</dd>
                  </div>
                </div>
                {issueLabel && (
                  <div className="flex items-start gap-2">
                    <Icon icon="lucide:hash" className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                        {t('magazine.meta.issue')}
                      </dt>
                      <dd className="text-ink font-semibold">{issueLabel}</dd>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Icon icon="lucide:calendar" className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                      {t('magazine.meta.date')}
                    </dt>
                    <dd className="text-ink font-semibold">
                      <time dateTime={mag.date}>{formattedDate}</time>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Icon icon="lucide:user" className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                      {t('magazine.meta.interviewee')}
                    </dt>
                    <dd className="text-ink font-semibold">{mag.interviewee}</dd>
                  </div>
                </div>
              </dl>

              {externalHref && (
                <a
                  href={externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-primary text-surface px-5 py-2.5 rounded-yada-full text-sm font-semibold hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 shrink-0"
                >
                  <Icon icon="lucide:external-link" />
                  {t('magazine.viewer.openExternal')}
                  <Icon icon="lucide:arrow-up-right" />
                </a>
              )}
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Zoomable clipping */}
      <section
        className="py-section-y px-section-x bg-surface"
        aria-labelledby="magazine-detail-title"
      >
        <Container size="lg">
          <Reveal>
            <h2 id="magazine-detail-title" className="sr-only">
              {mag.title}
            </h2>
            <ImageViewer
              src={mag.coverImage}
              alt={coverAlt}
              naturalWidth={mag.coverWidth}
              naturalHeight={mag.coverHeight}
            />
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
              <Link
                to="/magazine"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
              >
                <Icon icon="lucide:arrow-left" />
                {t('magazine.detail.backToArchive')}
              </Link>
              {externalHref && (
                <a
                  href={externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
                >
                  <Icon icon="lucide:external-link" />
                  {t('magazine.viewer.openExternal')}
                </a>
              )}
            </div>
          </Reveal>
        </Container>
      </section>
    </article>
  )
}